/* eslint-disable camelcase */
import * as api from '../../api/campaigns';
import * as mutationTypes from './mutationTypes';
import * as numbersMutationTypes from '../numbers/mutationTypes';
import { changeObjectKeysToCamelCase, changeObjectKeysToSnakeCase, getErrorMessage } from '../../helpers/functions';
import { getSampleMessagesMinAndContentAttributes } from '../../helpers/campaigns/functions';
import {getAccountCatalog, getPaymentMethods} from "../../api/billing";
import {
  buildBillingObject,
  getCampaignCatalogRatePlanId,
  getDefaultPaymentMethod
} from "../../helpers/billing/functions";

const actions = {
  async fetchCampaigns({ commit, rootState }, brandId) {
    commit(mutationTypes.FETCH_CAMPAIGNS);
    const credentials = rootState.credentials;
    try {
      const { data: campaignsResponse } = await api.getCampaigns(credentials, brandId);
      const campaigns  = campaignsResponse.map(campaignResponse => {
        const campaign = changeObjectKeysToCamelCase(campaignResponse.vonage_api_campaign);
        campaign.linkedNumbersCount = campaignResponse.linked_numbers_count;
        campaign.id = campaignResponse.id;
        return campaign;
      });
      commit(mutationTypes.DONE_FETCHING_CAMPAIGNS, campaigns);
    } catch (e) {
      commit(mutationTypes.ERROR_FETCHING_CAMPAIGNS, { error: getErrorMessage(e) });
    }
  },

  async fetchCampaign({ commit, rootState }, { campaignId, brandId, mainAccountId = null }) {
    commit(mutationTypes.FETCH_CAMPAIGN);
    const credentials = rootState.credentials;

    try {
      const { data: campaignResponse } = mainAccountId !== null ? await api.getCampaignOfMainAccount(credentials, campaignId, brandId, mainAccountId) : await api.getCampaign(credentials, campaignId, brandId);
      const campaign = { ...changeObjectKeysToCamelCase(campaignResponse.vonage_api_campaign), linkedNumbersCount: null };
      campaign.id = campaignResponse.id;
      campaign.linkedNumbersCount = campaignResponse.linked_numbers_count;
      commit(mutationTypes.DONE_FETCHING_CAMPAIGN, campaign);
    } catch (e) {
      commit(mutationTypes.ERROR_FETCHING_CAMPAIGN, { error: getErrorMessage(e) });
    }
  },

  async createCampaign({ commit, rootState }, { brandId, campaign}) {
    commit(mutationTypes.CREATE_CAMPAIGN);    
    const credentials = rootState.credentials;

    try {
      const paymentMethods = await getPaymentMethods(rootState.credentials);
      const paymentMethodId = getDefaultPaymentMethod(paymentMethods.data).id;
      const accountCatalog = await getAccountCatalog(rootState.credentials);
      const campaignCatalogRatePlanId = getCampaignCatalogRatePlanId(accountCatalog.data);
      const isMultiLocationAccount = rootState.accountDetails.Locations.length > 1;
      const { data } = await api.createCampaign(credentials, brandId, { campaign_request: changeObjectKeysToSnakeCase({ ...campaign }),
      campaign_billing_request: buildBillingObject(rootState.accountDetails, rootState.credentials.accountId, isMultiLocationAccount, campaignCatalogRatePlanId.productRatePlanId, paymentMethodId)});
      commit(mutationTypes.DONE_CREATING_CAMPAIGN, { campaign: changeObjectKeysToCamelCase(data) });
    } catch (e) {
      const errorTitle = getErrorMessage(e, 'title');
      if (errorTitle === 'Conflict') {
        commit(mutationTypes.ERROR_CREATING_CAMPAIGN, { error: 'campaigns.campaignCreationConflictError' });
        return;
      }
      commit(mutationTypes.ERROR_CREATING_CAMPAIGN, { error: getErrorMessage(e) });
    }
  },

  async updateSampleMessages({ commit, rootState }, { campaign, brandId}) {
    commit(mutationTypes.UPDATE_SAMPLE_MESSAGES);
    const credentials = rootState.credentials;

    try {

      await api.updateCampaign(changeObjectKeysToSnakeCase({ ...campaign }), brandId, credentials);
      commit(mutationTypes.DONE_UPDATING_SAMPLE_MESSAGES, { campaign });
    } catch (e) {
      commit(mutationTypes.ERROR_UPDATING_SAMPLE_MESSAGES, { error: getErrorMessage(e) });
    }
  },

  async deleteCampaign({ commit, rootState }, { campaign, brandId, showCampaignList }) {
    commit(mutationTypes.DELETE_CAMPAIGN);
    const credentials = rootState.credentials;
    let fetchedCampaign;
    try {
      await api.deleteCampaign(campaign.id, brandId, credentials);
      if (showCampaignList) {
        const { data: campaignsResponse } = await api.getCampaigns(credentials, brandId);
        const campaigns  = campaignsResponse.map(campaignResponse => {
          const campaign = changeObjectKeysToCamelCase(campaignResponse.vonage_api_campaign);
          campaign.id = campaignResponse.id;
          return campaign;
        });
        commit(mutationTypes.DONE_FETCHING_CAMPAIGNS, campaigns);
      }
      commit(mutationTypes.DONE_DELETING_CAMPAIGN, {
        campaign: !showCampaignList ? fetchedCampaign : campaign,
        showCampaignList,
      });
    } catch (e) {
      commit(mutationTypes.ERROR_DELETING_CAMPAIGN, { error: getErrorMessage(e) });
    }
  },

  setCampaignInCreationUseCase({ commit }, usecase) {
    commit(mutationTypes.SET_CAMPAIGN_IN_CREATION_USECASE, { usecase });
  },

  async qualifyBrand({ commit, rootState }, { brandId, usecase }) {
    commit(mutationTypes.QUALIFY_BRAND);
    const credentials = rootState.credentials;

    try {
      const { data } = await api.qualifyBrand(usecase, brandId, credentials);
      commit(mutationTypes.DONE_QUALIFYING_BRAND, {
        qualification: changeObjectKeysToCamelCase(data),
        ...getSampleMessagesMinAndContentAttributes(data),
      });
    } catch (e) {
      commit(mutationTypes.ERROR_QUALIFYING_BRAND, { error: getErrorMessage(e) });
    }
  },

  removeLastCreatedCampaign({ commit }) {
    commit(mutationTypes.REMOVE_LAST_CREATED_CAMPAIGN);
  },
  removeLastDeletedCampaign({ commit }) {
    commit(mutationTypes.REMOVE_LAST_DELETED_CAMPAIGN);
  },

  removeDeletionErrors({ commit }, key) {
    commit(mutationTypes.REMOVE_DELETION_ERROR, { key });
  },

  removeCreationErrors({ commit }, key) {
    commit(mutationTypes.REMOVE_CREATION_ERRORS, { key });
  },

  async fetchLinkedCampaign({ commit, state: { linkedCampaigns } }, { campaignId, apiKey, brandId }) {
    if (linkedCampaigns[`${brandId}${campaignId}`]) {
      return;
    }
    try {
      const { data: campaign } = await api.getCampaign(campaignId, apiKey, brandId);
      commit(mutationTypes.ADD_LINKED_CAMPAIGN, campaign);
    } catch (e) {
      commit(mutationTypes.ERROR_HANDLING_NUMBER_LINK, { error: getErrorMessage(e) });
    }
  },

  async linkNumber({ commit, rootState }, { campaignId, phoneNumber}) {
    try {
      commit(mutationTypes.HANDLE_NUMBER_LINK);
      const credentials = rootState.credentials;
      const linkNumberResponse = await api.linkNumberToCampaign(credentials, campaignId, phoneNumber);
      commit(mutationTypes.DONE_HANDLING_NUMBER_LINK);
      commit(`numbers/${numbersMutationTypes.UPDATE_NUMBER}`, { phoneNumber, linkedNumber: changeObjectKeysToCamelCase(linkNumberResponse.data) });
    } catch (e) {
      commit(mutationTypes.ERROR_HANDLING_NUMBER_LINK, { error: getErrorMessage(e) });
    }
  },

  async unlinkNumber({ commit, rootState }, { campaignId, phoneNumber}) {
    try {
      commit(mutationTypes.HANDLE_NUMBER_LINK);
      const credentials = rootState.credentials;
      await api.unlinkNumberToCampaign(credentials, campaignId, phoneNumber);
      commit(mutationTypes.DONE_HANDLING_NUMBER_UNLINK);
      commit(`numbers/${numbersMutationTypes.UNLINK_NUMBER_FROM_CAMPAIGN}`, { phoneNumber });
    } catch (e) {
      commit(mutationTypes.ERROR_HANDLING_NUMBER_LINK, { error: getErrorMessage(e) });
    }
  },

  clearNumbers({ commit }) {
    commit(mutationTypes.CLEAR_NUMBERS);
  },

  clearLinkingErrors({ commit }) {
    commit(mutationTypes.CLEAR_ERROR_HANDLING_NUMBERS);
  },

  removeUpdateErrors({ commit }, key) {
    commit(mutationTypes.REMOVE_UPDATE_ERRORS, { key });
  },

  resetForm({ commit }) {
    commit(mutationTypes.RESET_FORM);
  },
};

export default actions;
